import React, { useState } from 'react';

import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaEuropaceOffer } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { isEuropaceOfferAcceptable } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuArrowRight } from 'react-icons/lu';

import { useAcceptOffer } from '~/features/leadDetails/hooks/useAcceptOffer';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useEuropaceProcessId } from '~/features/leadDetails/hooks/useEuropaceProcessId.ts';
import { useOffersAcceptanceStatusByLoanRequestId } from '~/features/leadDetails/hooks/useOffersAcceptanceStatusByLoanRequestId';
import { useOffersByRequestId } from '~/features/leadDetails/hooks/useOffersByRequestId';

type Props = {
    currentRequestId?: string;
    lead: SchemaLoanRequestResolver_Get;
};

export const AcceptAllOffersButton: React.FC<Props> = ({ currentRequestId, lead }) => {
    const [hasAcceptedAllOffers, setHasAcceptedAllOffers] = useState(false);
    const { t } = useTranslation();
    const { permissions } = useAclPermissions({ loanRequestId: lead.id });
    const { data: offerRequestArray } = useOffersByRequestId({ id: lead.id, requestId: currentRequestId });
    const { data: europaceProcessId } = useEuropaceProcessId(lead.id);
    const { data: offersAcceptanceStatus } = useOffersAcceptanceStatusByLoanRequestId({
        loanRequestId: lead.id,
        source: LOAN_REQUEST_OFFER_SOURCES.EUROPACE,
    });
    const acceptOffer = useAcceptOffer({
        currentRequestId,
        europaceProcessId,
        lead,
        source: LOAN_REQUEST_OFFER_SOURCES.EUROPACE,
    });

    const europaceOffers = offerRequestArray?.filter(
        (offersResponse) => offersResponse.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE,
    );

    if (!offerRequestArray || !europaceProcessId) return null;

    const acceptableOffers = europaceOffers?.reduce((acc, offersResponse) => {
        const offers = offersResponse.offers.filter((offer) => {
            const offerAcceptanceStatus = offersAcceptanceStatus?.find((item) => item.id === offer.id);
            return (
                isEuropaceOfferAcceptable({
                    completenessStatus: offer.vollstaendigkeit?.status,
                    feasibilityStatus: offer.machbarkeit?.status,
                }) && !offerAcceptanceStatus?.acceptanceStatus
            );
        });
        return [...acc, ...offers];
    }, [] as SchemaEuropaceOffer[]);

    const isAtLeastOneOfferAcceptable = acceptableOffers && acceptableOffers.length > 0;

    const acceptAllOffers = async () => {
        acceptableOffers?.forEach((offer) => {
            acceptOffer.mutate(offer);
        });
        setHasAcceptedAllOffers(true);
    };

    return (
        <Button
            variant={ButtonVariant.SECONDARY}
            iconLeft={<LuArrowRight />}
            onClick={acceptAllOffers}
            disabled={
                !isAtLeastOneOfferAcceptable ||
                hasAcceptedAllOffers ||
                !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].acceptOffer
            }
        >
            {t('BANK_CREDIT_REQUEST.OFFERS_TABLE.REQUEST_BANK_ACCEPTANCE_ALL')}
        </Button>
    );
};
